import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useUserActivity from './useUserActivity';
import usePageVisibility from './usePageVisibility';
import useExponentialBackoff from './useExponentialBackoff';

const useAdaptivePolling = ({ 
  pollingFunction, 
  enabledRoutes = [], 
  context = 'general'
}) => {
  const location = useLocation();
  const isPageVisible = usePageVisibility();
  const userActivity = useUserActivity();
  const backoff = useExponentialBackoff();
  
  const intervalRef = useRef(null);
  const [currentInterval, setCurrentInterval] = useState(userActivity.pollingInterval);
  const [isPolling, setIsPolling] = useState(false);
  const lastFastPollRef = useRef(0);

  // Check if current route should enable polling
  const shouldPollOnRoute = () => {
    if (enabledRoutes.length === 0) return true; // No route restrictions
    return enabledRoutes.some(route => location.pathname.includes(route));
  };

  // Determine if we should poll based on all conditions
  const shouldPoll = () => {
    return (
      userActivity.shouldPoll &&     // User activity and online status
      isPageVisible &&               // Page is visible
      shouldPollOnRoute()            // On correct route
    );
  };

  // Get appropriate polling interval with fast poll logic
  const getPollingInterval = () => {
    const now = Date.now();
    const timeSinceFastPoll = now - lastFastPollRef.current;
    
    // If we recently triggered fast polling, use progressive intervals
    if (timeSinceFastPoll < 60000) { // Within 1 minute of fast poll trigger
      if (timeSinceFastPoll < 10000) return 3000;      // 3s for first 10 seconds
      if (timeSinceFastPoll < 30000) return 5000;      // 5s for next 20 seconds  
      if (timeSinceFastPoll < 60000) return 10000;     // 10s for next 30 seconds
    }
    
    // Otherwise use activity-based interval
    return userActivity.pollingInterval;
  };

  // Trigger fast polling (e.g., after sending message)
  const triggerFastPoll = () => {
    lastFastPollRef.current = Date.now();
    userActivity.triggerActivity(); // Also trigger user activity
    
    // If currently polling, restart with fast interval
    if (isPolling) {
      stopPolling();
      startPolling();
    }
  };

  // Execute polling with error handling
  const executePoll = async () => {
    if (!shouldPoll()) {
      return;
    }

    const result = await backoff.executeWithBackoff(pollingFunction);
    
    if (result.success) {
      // Success - continue normal polling
      return;
    }
    
    if (result.maxRetriesReached) {
      // Stop polling after max retries
      stopPolling();
      return;
    }
    
    if (result.shouldRetry) {
      // Schedule retry with exponential backoff
      setTimeout(() => {
        if (isPolling) {
          executePoll();
        }
      }, result.retryDelay);
    }
  };

  // Start polling
  const startPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    const interval = getPollingInterval();
    setCurrentInterval(interval);
    setIsPolling(true);
    
    // Initial poll
    executePoll();
    
    // Set up interval
    intervalRef.current = setInterval(executePoll, interval);
  };

  // Stop polling
  const stopPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPolling(false);
    backoff.reset();
  };

  // Restart polling with new interval if needed
  const updatePollingInterval = () => {
    if (!isPolling) return;
    
    const newInterval = getPollingInterval();
    if (newInterval !== currentInterval) {
      startPolling(); // Restart with new interval
    }
  };

  // Effect to manage polling lifecycle
  useEffect(() => {
    if (shouldPoll()) {
      if (!isPolling) {
        startPolling();
      } else {
        updatePollingInterval();
      }
    } else {
      if (isPolling) {
        stopPolling();
      }
    }
  }, [
    userActivity.shouldPoll,
    userActivity.pollingInterval,
    isPageVisible,
    location.pathname
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);


  return {
    isPolling,
    currentInterval,
    activityState: userActivity.activityState,
    triggerFastPoll,
    pollNow: executePoll,
    startPolling,
    stopPolling
  };
};

export default useAdaptivePolling;